/** @format */

import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Inject,
	Input,
	NgZone,
	OnChanges,
	OnInit,
	Output,
	PLATFORM_ID,
	SimpleChanges
} from '@angular/core';

import { fromEvent, Subject } from 'rxjs';
import { debounceTime, filter, first, map, take, takeUntil } from 'rxjs/operators';

import {
	AnalyticsService,
	DeviceService,
	EmitEvent,
	EventBusService,
	Events,
	UtilityService,
	WINDOW
} from '@shared/utils/services';

import {
	Chain,
	KillSwitches,
	CriteoViewItemMetadata,
	PromoteIQ,
	CriteoViewItemResponse,
	CriteoViewProductsResponse
} from '@shared/data-access/interfaces';
import {
	CertonaRecommendations,
	CertonaRecommendationsResponse,
	CertonaScheme,
	CertonaSchemeItem,
	CertonaService,
	RecommendationDataService
} from '@product-recommendations/data-access';
import { HmfCarouselComponent, HmfProductCardComponent } from '@dsg-tech/homefield-angular-cdk';
import { HmfButtonComponent, HmfSkeletonComponent } from '@dsg-tech/homefield-angular';
import { ItemNamePipe } from '@plp/utils';
import { CriteoService, PromoteIQService, Store } from '@shared/data-access/services';
import { ATC_BUTTON_TEXTS } from '@pdp/add-to-cart/data-access';

@Component({
	selector: 'product-recommendations-components-product-recommendations',
	templateUrl: './product-recommendations.component.html',
	styleUrls: ['./product-recommendations.component.scss'],
	imports: [
		CommonModule,
		HmfButtonComponent,
		HmfCarouselComponent,
		HmfProductCardComponent,
		HmfSkeletonComponent,
		ItemNamePipe
	],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductRecommendationsComponent implements OnInit, OnChanges {
	@Input() displayName: string;
	@Input() filterBy: string;
	@Input() schemes: CertonaScheme[];
	@Input() showViewMoreLink: boolean;
	@Input() imageSize = '';
	@Input() isOOSRecommendations = false;
	@Input() isOOSSimilarProducts = false;
	@Input() isAlternativeInStock = false;
	@Input() chainAbbr = '';
	@Input() showQVButtons = false;
	@Input() productRecommendationsQuickViewEnabled = false;
	@Input() showHeader = true;
	@Input() showATC = false;
	@Input() cardSize;
	@Input() isTabbedCarouselRecs = false;
	@Input() showSkeletonLoading = false;
	@Input() numberOfSkeletonCards = 8;
	@Input() isSidePanelRecs = false;
	@Input() isSidePanelRecsRedesign = false;
	@Input() showVertical = false;
	//Remove when all product info is in hmf-product-card
	@Input() showVerticalLegacy = false;
	/**
	 * @description
	 * Needed if Criteo is used for sponsored products. Only used for PDP.
	 */
	@Input() criteoViewItemMetadata: CriteoViewItemMetadata;

	/**
	 * @description
	 * Boolean flag representing if this component is being used on PLP.
	 */
	@Input() isPLPRecs = false;

	/**
	 * @description
	 * Boolean flag representing if this component is being used for cart OOS recs.
	 */
	@Input() isCartOOSRecs = false;

	/**
	 * @description
	 * Toggles sponsored recommendation visibility
	 */
	@Input() sponsored: boolean;

	/**
	 * @description
	 * Ecode and catgroupId needed if sponsored is set to true
	 */
	@Input() ecode: string;
	@Input() catgroupId: number;

	/**
	 * @description
	 * List of Promote IQ products used for PLP sponsored products.
	 */
	@Input() promoteIQProducts;

	@Input() pageType: string;

	@Input() homefieldCarouselEnabled = false;

	/**
	 * @description
	 * List of Criteo products used for PLP/SRLP sponsored products
	 */
	@Input() criteoProducts: CriteoViewProductsResponse;

	// Emit event when quick view is clicked
	@Output() quickViewClick = new EventEmitter<string>();

	/**
	 * @description
	 * Keeps track of which sponsored product impressions have already been called
	 */
	sponsoredImpressionHistory: { [key: string]: boolean } = {};

	/**
	 * @description
	 * Used to manage the scroll subscription if IntersectionObserver isn't supported
	 */
	scrollEventUnsubscribe$ = new Subject<void>();

	schemas: CertonaScheme[] = [];

	productDisplayLimit = 6;
	productDisplayMinimum = 6;
	productRecommendations: CertonaRecommendationsResponse;
	isPlaRecommendations = false;
	isMobile = false;
	certonaQuickviewEnabled: boolean;
	triggerSponsoredProductsShownEvent = false;
	atcButtonTexts = ATC_BUTTON_TEXTS;
	isUsingCriteoSPAAdServer = false;

	private readonly SPONSORED_PRODUCTS_ID = 'sponsored-product';

	constructor(
		@Inject(PLATFORM_ID) protected platformId: any,
		@Inject(WINDOW) private window: Window,
		private changeRef: ChangeDetectorRef,
		private productContainerService: CertonaService,
		private deviceService: DeviceService,
		private promoteIQService: PromoteIQService,
		private readonly criteoService: CriteoService,
		private analyticsService: AnalyticsService,
		private eventBusService: EventBusService,
		private store: Store,
		private chain: Chain,
		private ngZone: NgZone,
		public recommendationDataService: RecommendationDataService,
		private readonly utilityService: UtilityService
	) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (
			changes.promoteIQProducts &&
			changes.promoteIQProducts.currentValue &&
			this.sponsored &&
			this.isPLPRecs
		) {
			this.createPLPPromoteIQSponsoredProducts(changes.promoteIQProducts.currentValue, 6);
		}
	}

	ngOnInit(): void {
		this.store
			.selectDistinctKey<KillSwitches>('ENVIRONMENT_KILLSWITCH_VALUES')
			.pipe(take(1))
			.subscribe((killswitch) => {
				this.certonaQuickviewEnabled = this.utilityService.checkKillSwitch(
					'certonaQuickviewEnabled',
					killswitch,
					this.chain.chainIdentifierAbbr
				);

				this.triggerSponsoredProductsShownEvent = this.utilityService.checkKillSwitch(
					'triggerSponsoredProductsShownEvent',
					killswitch,
					this.chain.chainIdentifierAbbr
				);

				this.isUsingCriteoSPAAdServer = this.utilityService.checkKillSwitch(
					'isUsingCriteoSPAAdServer',
					killswitch,
					this.chain.chainIdentifierAbbr
				);
			});

		this.isMobile = this.deviceService.isMobile();

		if (this.showSkeletonLoading) {
			this.setSchemeToEmptyForSkeletonLoading();
		}

		if (this.sponsored) {
			this.isPlaRecommendations = true;
			if (this.isUsingCriteoSPAAdServer) {
				this.enableCriteo();
			} else {
				this.enablePromoteIQ();
			}
		} else {
			if (this.filterBy === 'pdplanding_rr') {
				this.isPlaRecommendations = true;
			}

			// Listen for product recommendations from Certona / Monetate.
			// Don't emit when recommendations don't include filtered schema, e.g. when skeleton is created for another schema
			// ...except when the recs come from Monetate, which sometimes doesn't include a schema if it's empty.
			// That gets handled in parseCertonaRecommendations().
			const certonaRecommendations$ = this.filterBy
				? this.productContainerService.certonaRecommendationsState.pipe(
						filter(
							(res) =>
								res?.certonaRecommendations?.resonance?.schemes?.find(
									(s) => s.scheme === this.filterBy
								) || res?.certonaRecommendations?.source === 'Monetate'
						)
					)
				: this.productContainerService.certonaRecommendationsState;

			certonaRecommendations$
				.pipe(take(4))
				.subscribe((recommendations: CertonaRecommendationsResponse) => {
					if (this.filterBy !== 'cartempty_rr') {
						this.productRecommendations = recommendations;
					}
					if (isPlatformBrowser(this.platformId) || this.isPlaRecommendations) {
						// Parse the product recommendations;
						this.parseCertonaRecommendations(recommendations);
					}
					if (
						recommendations &&
						recommendations.certonaRecommendations.source !== 'skeleton'
					) {
						this.callBttApis('end');
					}
				});
		}

		if (!this.isAlternativeInStock && this.isOOSRecommendations) {
			this.fireAnalyticsEventForOOSRec();
		}
		this.callBttApis('start');
	}

	enablePromoteIQ(): void {
		if (isPlatformBrowser(this.platformId)) {
			const SLOT_NUMBER = this.isMobile ? 14055 : 14050; // static number provided by PIQ (One for Desktop and one for Mobile)
			this.productDisplayLimit = 7; // number of product ads requested
			const store = this.chainAbbr === 'gg' ? this.chainAbbr : '';

			const ecodeList = [];
			if (
				this.isPLPRecs &&
				this.ecode === undefined &&
				this.promoteIQProducts !== undefined
			) {
				this.promoteIQProducts.forEach((product) => {
					const productId =
						this.chainAbbr === 'gg'
							? `${this.chainAbbr}_${product.product_id}`
							: product.product_id;
					ecodeList.push(productId);
				});
			}

			const params = this.promoteIQService.createParams(
				{ match_products: this.ecode ? this.ecode : ecodeList },
				this.productDisplayLimit,
				SLOT_NUMBER,
				this.catgroupId,
				store
			);
			this.promoteIQService
				.getPromoteIQData$(params)
				.pipe(
					first(),
					filter((res) => res && res.length > 0),
					map((res: PromoteIQ[]) => {
						const schema = {
							items: [],
							certonaDisplayText: '',
							display: '',
							explanation: '',
							scheme: ''
						};
						for (const rec of res) {
							schema.items.push({
								...rec.product,
								impression_tracker: rec.impression_tracker,
								click_tracker: rec.click_tracker,
								BreakingMap: ''
							});
						}
						return this.setRecommendationScheme(schema);
					})
				)
				.subscribe((schema: CertonaScheme) => {
					// Add the current schema to the List
					this.analyticsService.triggerAnalyticsEvent('PromoteIQloaded');
					this.schemas = [schema];
					this.changeRef.detectChanges();

					if (this.utilityService.hasIntersectionObserverSupport()) {
						setTimeout(() => {
							this.createIntersectionObservers(schema);
						}, 10);
					} else {
						setTimeout(() => {
							this.createScrollSubscription(schema);
						}, 10);
					}
				});
		}
	}

	enableCriteo(): void {
		this.productDisplayLimit = 14;

		if (this.criteoViewItemMetadata) {
			// For PDP
			this.criteoService.viewItem(this.criteoViewItemMetadata).subscribe({
				next: (res) => {
					this.organizeCriteoProductsIntoSchema(res);
				},
				error: (err) => {
					console.error('Error fetching Criteo data:', err);
				},
				complete: () => {
					this.changeRef.detectChanges();
				}
			});
		} else {
			// for PLP/SRLP
			this.organizeCriteoProductsIntoSchema(this.criteoProducts);
		}
	}

	organizeCriteoProductsIntoSchema(res: CriteoViewItemResponse | CriteoViewProductsResponse) {
		const schema = {
			items: [],
			certonaDisplayText: '',
			display: '',
			explanation: '',
			scheme: ''
		};
		const carousel = res.placements?.[0]?.carousel?.[0];
		const products = carousel?.products;
		if (products?.length >= this.productDisplayMinimum) {
			// if this.criteoProducts exists, the onLoadBeacon was already fired by the page resolve guard
			if (!this.criteoProducts) {
				this.criteoService.fireBeacon(carousel.OnLoadBeacon);
			}
			schema.items = this.criteoService.transformCriteoProducts(products, true);
			this.setRecommendationScheme(schema);
			this.schemas = [schema];
			// Create events for when the carousel and items scroll into view that will fire the appropriate Beacons
			if (this.utilityService.hasIntersectionObserverSupport()) {
				setTimeout(() => {
					this.createIntersectionObservers(schema, carousel.OnViewBeacon);
				}, 10);
			} else {
				setTimeout(() => {
					this.createScrollSubscription(schema, carousel.OnViewBeacon);
				}, 10);
			}
		} else {
			console.warn(
				`Criteo response does not contain enough products: ${products?.length} products returned.`
			);
			schema.display = 'no';
		}
		this.schemas = [schema];
		this.changeRef.detectChanges();
	}

	parseCertonaRecommendations(response: CertonaRecommendationsResponse): void {
		// Check to ensure there is a valid response to parse
		if (!response?.certonaRecommendations?.resonance?.schemes) return;

		const schemas = response.certonaRecommendations.resonance.schemes.filter(
			(schema) => schema.scheme === this.filterBy
		);

		if (schemas.length === 0 && response.certonaRecommendations.source === 'Monetate') {
			// If Monetate recs didn't include this scheme, treat it as if it included an empty one.
			// This is because Monetate sometimes leaves out empty schemas altogether,
			// instead of sending empty ones with `display: "no"` like Certona does.
			schemas.push({
				display: 'no',
				explanation: '',
				items: [],
				scheme: this.filterBy
			});
		}

		const transformedSchemas = schemas.map((schema) => {
			if (this.filterBy === 'oosproduct_rr') {
				this.productDisplayLimit = 3;
			} else {
				this.productDisplayLimit = schema.items.length;
			}

			return this.setRecommendationScheme(schema);
		});

		// Check to see if there are any schemas to display
		if (transformedSchemas.length > 0) {
			this.schemas = transformedSchemas;

			// Trigger change detection
			this.changeRef.detectChanges();
		}
	}

	/**
	 * @description
	 * Used to determine if a sponsored recommendation card is within view
	 * If a card is within view we will notify Promote IQs impression tracker
	 */
	createIntersectionObservers(schema: CertonaScheme, criteoOnViewBeacon?: string) {
		// Intersection Observer for analytics impression tracker
		// Anaytics impression tracker needs to know when sponsored recs container is in view

		const sponsoredProductsContainer = document.getElementById(this.SPONSORED_PRODUCTS_ID);
		const analyticsCallBack = () => {
			if (this.triggerSponsoredProductsShownEvent) {
				this.analyticsService.triggerAnalyticsEvent('SponsoredProductsShown');
			}
		};
		this.utilityService.singleViewIntersectionObserver(
			sponsoredProductsContainer,
			analyticsCallBack
		);

		// Placement level (carousel) OnViewBeacon call to Criteo
		if (criteoOnViewBeacon) {
			const criteoCarouselCallBack = () => {
				this.criteoService.fireBeacon(criteoOnViewBeacon);
			};
			this.utilityService.singleViewIntersectionObserver(
				sponsoredProductsContainer,
				criteoCarouselCallBack,
				0.5 // Criteo requires us to have 50% of the product in view before firing the beacon
			);
		}

		// Intersection Observer for Promote IQ impression tracker
		// Promote IQ impression tracker needs to know when each individual product rec is in view
		// Criteo fires the individual product view beacon once there is 50% of the product in view
		for (let i = 0, length = schema.items.length; i < length; ++i) {
			const item = schema.items[i];
			const trackingId = `piq-${item.ID}`;
			const sponsoredProductCard = document.getElementById(trackingId);
			if (this.isUsingCriteoSPAAdServer) {
				const criteoCallback = () => {
					this.criteoService.fireBeacon(item.impression_tracker);
				};

				this.utilityService.singleViewIntersectionObserver(
					sponsoredProductCard,
					criteoCallback,
					0.5
				);
			} else {
				const promoteIQCallback = () => {
					this.promoteIQService.fireTrackingEvent$(item.impression_tracker);
				};

				this.utilityService.singleViewIntersectionObserver(
					sponsoredProductCard,
					promoteIQCallback
				);
			}
		}
	}

	/**
	 * @description
	 * Tranforms schema pricing to be consumable by Angular currency pipe in the HTML
	 * Also sets schema.certonaDisplayText based on schema.explanation or displayName variable
	 */
	setRecommendationScheme(schema: CertonaScheme): CertonaScheme {
		// Iterate through the current schema items
		for (let i = 0, length = schema.items.length; i < length; ++i) {
			const item = schema.items[i];
			// Check to see if the item is valid
			if (item) {
				// parsing price in case comma is there
				item.maxmapprice = item.maxmapprice?.replace(',', '');
				item.maxofferprice = item.maxofferprice?.replace(',', '');
				item.minmapprice = item.minmapprice?.replace(',', '');
				item.minofferprice = item.minofferprice?.replace(',', '');
				item.offerprice = item.offerprice?.replace(',', '');
				item.mapPriceIndicator = item?.BreakingMap === 'Y' ? 1 : 0;
			}
		}

		// Default the display text to undefined
		schema.certonaDisplayText = undefined;

		// Check to see if a display text has been provided
		if (typeof schema.explanation === 'string' && schema.explanation.trim() !== '') {
			schema.certonaDisplayText = schema.explanation;
		} else if (typeof this.displayName === 'string' && this.displayName.trim() !== '') {
			schema.certonaDisplayText = this.displayName;
		}

		schema['cardSize'] = this.getProductCardSize(schema.certonaDisplayText, this.isMobile);

		return schema;
	}

	/**
	 * @description
	 * Determines the size of the product card based on the certona display text and if the user is on mobile
	 * @param certonaDisplayText - The display text provided by certona
	 * @param isMobile - Boolean flag representing if the user is on mobile
	 * @returns - The size of the product card
	 */
	getProductCardSize(certonaDisplayText: string, isMobile: boolean): string {
		if (certonaDisplayText === 'Recently Viewed' && isMobile) {
			return 'small';
		}
		if (
			certonaDisplayText === 'Similar Products to Consider' ||
			certonaDisplayText === 'Try These Instead'
		) {
			return 'medium';
		}

		if (this.isCartOOSRecs) {
			return isMobile ? 'small' : 'medium';
		}

		return isMobile ? 'medium' : 'large';
	}

	/**
	 * @description
	 * Creates subscription for scroll method
	 * The method will be used as an alternative for IntersectionObserver
	 */

	createScrollSubscription(schema: CertonaScheme, criteoOnViewBeacon?: string): void {
		let animationFrameId: number;

		this.ngZone.runOutsideAngular(() => {
			const handleScroll = () => {
				animationFrameId = requestAnimationFrame(() => {
					// Anaytics impression tracker just needs to know when sponsored recs container is in view
					const isSponsoredProductContainerInView =
						this.utilityService.isElementInViewPort(
							document.getElementById(this.SPONSORED_PRODUCTS_ID)
						);

					if (
						isSponsoredProductContainerInView &&
						!this.sponsoredImpressionHistory[this.SPONSORED_PRODUCTS_ID]
					) {
						if (this.triggerSponsoredProductsShownEvent) {
							this.analyticsService.triggerAnalyticsEvent('SponsoredProductsShown');
						}
						if (criteoOnViewBeacon) {
							this.criteoService.fireBeacon(criteoOnViewBeacon);
						}
						this.sponsoredImpressionHistory[this.SPONSORED_PRODUCTS_ID] = true;
					}

					// Promote IQ impression tracker needs to know when each individual product rec is in view
					for (let i = 0, length = schema.items.length; i < length; ++i) {
						const item = schema.items[i];
						const trackingId = `piq-${item.ID}`;

						const isSponsoredProductCardInView =
							this.utilityService.isElementInViewPort(
								document.getElementById(trackingId)
							);

						if (
							isSponsoredProductCardInView &&
							!this.sponsoredImpressionHistory[trackingId]
						) {
							this.sponsoredImpressionHistory[trackingId] = true;
							if (this.isUsingCriteoSPAAdServer) {
								this.criteoService.fireBeacon(item.impression_tracker);
							} else {
								this.promoteIQService.fireTrackingEvent$(item.impression_tracker);
							}
						}
					}

					// If our promote iq impression history matches the number of displayed products plus our one analytics impression then we no longer need our scroll event
					if (
						Object.keys(this.sponsoredImpressionHistory).length ===
						this.productDisplayLimit + 1
					) {
						this.scrollEventUnsubscribe$.next();
						this.scrollEventUnsubscribe$.complete();
					}
				});
			};

			fromEvent(this.window, 'scroll')
				.pipe(takeUntil(this.scrollEventUnsubscribe$), debounceTime(250))
				.subscribe(() => {
					// Cancel the previously scheduled animation frame callback
					cancelAnimationFrame(animationFrameId);

					// Call the scroll event handler
					handleScroll();
				});
		});
	}

	fireAnalyticsEventForOOSRec(): void {
		this.analyticsService.triggerAnalyticsEvent('OOSRecProduct', 'OOSRecProduct');
	}

	openQuickviewPanel(ecode: string, url: string): void {
		// Get recid query param from URL
		const urlParams = new URLSearchParams(url ? url.split('?')[1] : '');
		const dataAndCallBack = {
			additionalData: urlParams.get('recid'),
			callback: this.onQuickViewAddToCartSuccess.bind(this)
		};

		this.quickViewClick.emit(ecode);
		window.quickview.openProductQuickView(ecode, 'certona', dataAndCallBack);
	}

	/**
	 * @description
	 * Blue Triangle tagging for certona containers.
	 * start() called on page load.
	 * end() called when response from api recieved and component rendered.
	 */
	callBttApis(apiName: string): void {
		if (
			isPlatformBrowser(this.platformId) &&
			(this.pageType === 'plp' || this.pageType === 'srlp') &&
			this.filterBy &&
			this.window['bttUT']
		) {
			this.window['bttUT'][apiName]({
				pageName: `angular ${this.pageType} - certona`,
				txnName: 'eCommerce'
			});
		}
	}

	/**
	 * @description Event handler for when a homefield card is clicked
	 * @param item - The CertonaSchemeItem that was clicked
	 */
	handleProductCardClicked(item: CertonaSchemeItem): void {
		if (!item) return;

		if (this.productRecommendationsQuickViewEnabled) {
			this.openQuickviewPanel(item.ID, item.URL);
		} else if (this.isUsingCriteoSPAAdServer) {
			this.goToProductUrl(item.URL);
		} else {
			this.goToProductUrl(item.click_tracker ? item.click_tracker + item.URL : item.URL);
		}
	}

	goToProductUrl(url: string): void {
		this.window.location = url;
	}

	trackByCertona(index: number, item: CertonaSchemeItem): string {
		if (!item) return index.toString();
		return index.toString() + item.ID + (item.SKUID ? item.SKUID : '');
	}

	trackBySchema(index: number, item: CertonaSchemeItem): string {
		return index.toString() + (item ? item.ID + item.WCSIdentifier : '');
	}

	/**
	 * @description Set the schema to empty for skeleton loading
	 * This will ensure that the product recommendations are not displayed until new data is received
	 */
	setSchemeToEmptyForSkeletonLoading(): void {
		// Set recs to empty at the service level to ensure nothing shows until we get new data
		const emptyRecs = {
			resonance: {
				schemes: [
					{
						scheme: this.filterBy || 'skeleton',
						explanation: '',
						display: 'yes',
						items: new Array(this.numberOfSkeletonCards).fill(null)
					}
				]
			},
			source: 'skeleton'
		} as CertonaRecommendations;

		this.productContainerService.recommendations(emptyRecs);

		// If skeleton loaders still showing after 10 seconds, remove them
		if (isPlatformBrowser(this.platformId)) {
			// Timeouts should only be in client only
			setTimeout(() => {
				if (
					this.productRecommendations &&
					this.productRecommendations.certonaRecommendations.source === 'skeleton'
				) {
					this.productContainerService.recommendations({
						resonance: {
							schemes: [
								{
									scheme: this.filterBy || 'skeleton',
									explanation: '',
									display: 'no',
									items: []
								}
							]
						},
						source: 'skeleton'
					});
				}
			}, 10000);
		}
	}

	/**
	 * @description
	 * Event handler for the QuickView "ATC Success Event".  It will emit an Event
	 * on the Event Bus to be picked up by any listeners for additional processing.
	 */
	private onQuickViewAddToCartSuccess() {
		this.eventBusService.emit(new EmitEvent(Events.PostATCEvent));
	}

	/**
	 * @description
	 * This function is used to format promote IQ product data into CertonaScheme type
	 * which will then be used to render the sponsored products for PLP.
	 */
	private createPLPPromoteIQSponsoredProducts(
		promoteIQProducts: PromoteIQ[],
		numProductsToShow: number
	): void {
		promoteIQProducts = promoteIQProducts.slice(0, numProductsToShow);
		const schema = {
			items: [],
			certonaDisplayText: '',
			display: '',
			explanation: '',
			scheme: ''
		};
		for (const rec of promoteIQProducts) {
			schema.items.push({
				...rec.product,
				impression_tracker: rec.impression_tracker,
				click_tracker: rec.click_tracker,
				BreakingMap: ''
			});
		}
		const certonaSchema: CertonaScheme = this.setRecommendationScheme(schema);

		this.analyticsService.triggerAnalyticsEvent('PromoteIQloaded');
		this.schemas = [certonaSchema];
		this.changeRef.detectChanges();
	}

	goToRecommendedForYou(): void {
		// Go to Recommended For You page /s/recommended-for-you
		this.window.location.href = '/s/recommended-for-you';
	}

	productHasDiscount(item: CertonaSchemeItem): boolean {
		return (
			(item.maxofferprice < item.maxlistprice || item.minofferprice < item.minlistprice) &&
			this.isVerticalGrid
		);
	}

	get isVerticalGrid(): boolean {
		return this.showVertical || this.showVerticalLegacy;
	}
}
